import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaRegArrowAltCircleDown,
  FaRegArrowAltCircleUp,
  FaRegMoneyBillAlt,
  FaInfoCircle,
  FaChartPie,
  FaExchangeAlt,
  FaRegEdit,
  FaDownload,
} from "react-icons/fa";
import { BsBoxArrowInDown, BsBoxArrowUpRight } from "react-icons/bs";
import { AiOutlineSearch, AiOutlineFundProjectionScreen } from "react-icons/ai";
import { MdOutlineFilterList, MdPrint, MdOutlineInsights } from "react-icons/md";
import { IoWalletSharp } from "react-icons/io5";
import { TbZoomMoney, TbHistory } from "react-icons/tb";
import { GiWallet } from "react-icons/gi";
import { Pie, Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler,
} from "chart.js";
import axios from "axios";
import clsx from "clsx";

// Transition Panel Icons
import {
  BsArrowDownCircle,
  BsArrowUpCircle,
  BsBoxArrowInDownRight,
} from "react-icons/bs";
import { HiOutlineCurrencyRupee } from "react-icons/hi2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler
);

interface Party {
  id: number;
  name: string;
  date: string;
  number?: string;
  contact_person_name?: string;
  contact_person_number?: string;
  categories?: string | string[];
  total_amount?: number;
}
interface Stock {
  id: number | string;
  party_id: number;
  categories: any[];
  date?: string;
  total_amount: number | string;
  indirect_charges?: number | string;
}
interface Withdrawal {
  id: number;
  party_id: number;
  amount: number;
  date: string;
  catrgories?: string;
}
interface StockHistory {
  id: number;
  stock_id: string | number;
  cat_id?: string | number;
  current_price: number | string;
  current_price_date: string;
}
interface Category {
  id: string | number;
  category_name: string;
}

type TransactionType = "buy" | "sell" | "withdraw";

interface Transaction {
  id: number | string;
  party_id: number | string;
  date: string;
  type: TransactionType;
  stock_id?: number | string;
  category_id?: number | string;
  qty: number;
  unit?: string;
  amount: number;
  remark?: string;
  current_price?: number;
  // For sell-specific:
  indirect_charges?: number;
}

type FilterState = {
  from?: string;
  to?: string;
  search?: string;
  type?: string;
  stock_id?: string;
  category_id?: string;
  party_id?: string | number;
  minAmount?: string;
  maxAmount?: string;
  quick?: string;
};

// ========== ADVANCED HELPERS ==========

function formatMoney(
  val: number | undefined,
  opts?: { compact?: boolean; symbol?: boolean }
) {
  if (!val || isNaN(val)) return opts?.symbol ? "₹0" : "0";
  if (opts?.compact) {
    const absVal = Math.abs(val);
    if (absVal >= 1e7)
      return (opts?.symbol ? "₹" : "") + (val / 1e7).toFixed(2) + "Cr";
    if (absVal >= 1e5)
      return (opts?.symbol ? "₹" : "") + (val / 1e5).toFixed(2) + "L";
    if (absVal >= 1e3)
      return (opts?.symbol ? "₹" : "") + (val / 1e3).toFixed(1) + "k";
  }
  return (
    (opts?.symbol ? "₹" : "") +
    Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })
  );
}

function downloadCSV(transactions: Transaction[], parties: Party[], format: "csv" | "excel" = "csv") {
  let text = "";
  if (format === "csv") {
    const csvHeader = "Date,Stock,Category,Party,Type,Qty,Unit,Amount,Remark\n";
    const rows = transactions.map((t) => {
      const partyObj = parties.find((p) => String(p.id) === String(t.party_id));
      return [
        t.date,
        t.stock_id,
        t.category_id,
        typeof partyObj?.name === "object" ? "" : partyObj?.name ?? "",
        t.type,
        t.qty,
        typeof t.unit === "object" ? "-" : t.unit ?? "-",
        t.amount,
        typeof t.remark === "object" ? "-" : t.remark ?? "",
      ].join(",");
    });
    text = csvHeader + rows.join("\n");
  } else {
    const excelHeader = "Date\tStock\tCategory\tParty\tType\tQty\tUnit\tAmount\tRemark\n";
    const rows = transactions.map((t) => {
      const partyObj = parties.find((p) => String(p.id) === String(t.party_id));
      return [
        t.date,
        t.stock_id,
        t.category_id,
        typeof partyObj?.name === "object" ? "" : partyObj?.name ?? "",
        t.type,
        t.qty,
        typeof t.unit === "object" ? "-" : t.unit ?? "-",
        t.amount,
        typeof t.remark === "object" ? "-" : t.remark ?? "",
      ].join("\t");
    });
    text = excelHeader + rows.join("\n");
  }

  const blob = new Blob([text], {
    type:
      format === "csv"
        ? "text/csv"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `transactions_${Date.now()}.${format === "csv" ? "csv" : "xls"}`;
  link.click();
  URL.revokeObjectURL(url);
}

// Transaction Detail modal remains the same

function TransactionDetailModal({
  transaction,
  onClose,
  stock,
  category,
  party,
}: {
  transaction: Transaction;
  onClose: () => void;
  stock: Stock | undefined;
  category: Category | any;
  party: Party | undefined;
}) {
  if (!transaction) return null;
  // Show indirect_charges if sell
  return (
    <div className="fixed z-50 inset-0 bg-[#1119]/50 flex items-center justify-center">
      <div className="bg-white shadow-2xl text-[#374151] p-6 rounded-lg min-w-[340px] max-w-[96vw] relative animate-fade-in">
        <button
          className="absolute top-2 right-2 text-xl text-[#868cab] hover:text-[#222] p-0.5"
          onClick={onClose}
        >
          ×
        </button>
        <div className="flex items-center gap-2 mb-4">
          <FaInfoCircle className="text-[#38b6ff] text-xl" />
          <span className="font-semibold text-lg tracking-wide">
            Transaction Detail
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#f6f8fc] text-[#8ca8e2] text-xs font-bold">
              #{transaction.id}
            </span>
          </span>
        </div>
        <dl className="flex flex-wrap gap-2 mb-2 text-[13px]">
          <dt className="w-28 text-[#555] font-semibold">Type</dt>
          <dd>
            <span
              className={
                "px-2 py-1 rounded " +
                (transaction.type === "buy"
                  ? "bg-green-100 text-green-700"
                  : transaction.type === "sell"
                  ? "bg-red-100 text-red-600"
                  : "bg-yellow-50 text-yellow-800")
              }
            >
              {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
            </span>
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Date</dt>
          <dd>{transaction.date}</dd>
          <dt className="w-28 text-[#555] font-semibold">Stock</dt>
          <dd>
            {typeof stock?.id === "object" ? "-" : stock?.id ?? transaction.stock_id ?? "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Category</dt>
          <dd>
            {typeof category?.category_name === "object"
              ? "-"
              : category?.category_name ||
                (typeof category?.category === "object"
                  ? "-"
                  : category?.category) ||
                (typeof category?.stock_name === "object"
                  ? "-"
                  : category?.stock_name) ||
                (typeof transaction.category_id === "object"
                  ? "-"
                  : transaction.category_id) ||
                "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Party</dt>
          <dd>
            {typeof party?.name === "object"
              ? "-"
              : party?.name ??
                (typeof transaction.party_id === "object"
                  ? "-"
                  : transaction.party_id) ??
                "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Quantity</dt>
          <dd>{transaction.qty}</dd>
          <dt className="w-28 text-[#555] font-semibold">Unit</dt>
          <dd>
            {typeof transaction.unit === "object" ? "-" : transaction.unit ?? "-"}
          </dd>
          <dt className="w-28 text-[#555] font-semibold">Amount</dt>
          <dd>{formatMoney(transaction.amount, { symbol: true })}</dd>
          {transaction.type === "sell" && (
            <>
              <dt className="w-28 text-[#555] font-semibold">Indirect Charges</dt>
              <dd>
                {typeof transaction.indirect_charges === "number" || typeof transaction.indirect_charges === "string"
                  ? formatMoney(Number(transaction.indirect_charges), { symbol: true })
                  : "-"}
              </dd>
            </>
          )}
          <dt className="w-28 text-[#555] font-semibold">Remark</dt>
          <dd>
            {typeof transaction.remark === "object" ? "-" : transaction.remark ?? "-"}
          </dd>
        </dl>
        <div className="flex gap-2 mt-5">
          <button
            className="bg-[#eef2fd] border rounded px-3 py-1.5 text-xs font-semibold text-[#4e6189] hover:bg-[#e6f4ff]"
            onClick={() => {
              window.print();
            }}
          >
            <MdPrint className="inline mr-1 -mt-0.5" /> Print
          </button>
          <button
            className="bg-[#f7f7fb] border rounded px-3 py-1.5 text-xs font-semibold text-[#3e987a] hover:bg-[#e0fcf5]"
            onClick={() =>
              navigator.clipboard
                .writeText(JSON.stringify(transaction, null, 2))
                .then(() => alert("Transaction copied to clipboard!"))
            }
          >
            <FaRegEdit className="inline mr-1 -mt-0.5" /> Copy JSON
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== ADVANCED COMPARISON & QUICK FILTERS ==========

const QUICK_FILTERS: { key: string; label: string }[] = [
  { key: "last7", label: "Last 7 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "ytd", label: "YTD" },
  { key: "withdraw", label: "Withdrawals" },
  { key: "highValue", label: "High Value (>₹1L)" },
];

function applyQuickFilter(key: string): Partial<FilterState> {
  const today = new Date();
  if (key === "last7") {
    const to = today.toISOString().slice(0, 10);
    const from = new Date(today.setDate(today.getDate() - 6)).toISOString().slice(0, 10);
    return { from, to };
  } else if (key === "thisMonth") {
    const from = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .slice(0, 10);
    const to = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .slice(0, 10);
    return { from, to };
  } else if (key === "lastMonth") {
    const from = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      .toISOString()
      .slice(0, 10);
    const to = new Date(today.getFullYear(), today.getMonth(), 0)
      .toISOString()
      .slice(0, 10);
    return { from, to };
  } else if (key === "ytd") {
    const from = new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10);
    return { from };
  } else if (key === "withdraw") {
    return { type: "withdraw" };
  } else if (key === "highValue") {
    return { minAmount: String(1e5) };
  }
  return {};
}

// ========== ADVANCED FILTERS UI ==========

function AdvancedFilters({
  filters,
  setFilters,
  allParties,
  allStocks,
  allCategories,
}: {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  allParties: Party[];
  allStocks: Stock[];
  allCategories: Category[];
}) {
  const handleChange = (field: keyof FilterState, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value === "All" ? undefined : value,
    }));
  };

  return (
    <div className="bg-[#e5f2fb] border border-[#d5e8fc] rounded-md p-4 flex flex-wrap gap-4 items-end mb-4">
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Party</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.party_id ?? ""}
          onChange={(e) => handleChange("party_id", e.target.value)}
        >
          <option value="">All Parties</option>
          {allParties.map((p) => (
            <option key={p.id} value={p.id}>{typeof p.name === "object" ? "-" : p.name ?? p.id}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Type</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[110px] focus:ring"
          value={filters.type ?? "All"}
          onChange={(e) => handleChange("type", e.target.value)}
        >
          <option value="All">All Types</option>
          <option value="buy">Buy</option>
          <option value="withdraw">Withdraw</option>
          <option value="sell">Sell</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Stock</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.stock_id ?? ""}
          onChange={(e) => handleChange("stock_id", e.target.value)}
        >
          <option value="">All Stocks</option>
          {allStocks.map((s) => (
            <option key={s.id} value={s.id}>
              {typeof s.id === "object" ? "-" : s.id}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Category</label>
        <select
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.category_id ?? ""}
          onChange={(e) => handleChange("category_id", e.target.value)}
        >
          <option value="">All Categories</option>
          {allCategories.map((c) => (
            <option key={c.id} value={c.id}>
              {typeof c.category_name === "object"
                ? "-"
                : c.category_name || (typeof c.id === "object" ? "-" : c.id)}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">From Date</label>
        <input
          type="date"
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.from ?? ""}
          onChange={(e) => handleChange("from", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">To Date</label>
        <input
          type="date"
          className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring"
          value={filters.to ?? ""}
          onChange={(e) => handleChange("to", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Search</label>
        <input
          type="text"
          placeholder="Search by id/remark"
          className="px-2 py-1.5 rounded border text-sm min-w-[130px] focus:ring"
          value={filters.search ?? ""}
          onChange={(e) => handleChange("search", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Min Amt</label>
        <input
          type="number"
          className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring"
          value={filters.minAmount ?? ""}
          onChange={(e) => handleChange("minAmount", e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Max Amt</label>
        <input
          type="number"
          className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring"
          value={filters.maxAmount ?? ""}
          onChange={(e) => handleChange("maxAmount", e.target.value)}
        />
      </div>
      <div>
        <button
          className="mt-4 px-3 py-1.5 rounded bg-[#f2e3fe] text-[#6c46b7] font-bold text-xs border border-[#e2d6fb] shadow hover:bg-[#e9fafe] transition"
          onClick={() => setFilters({})}
          type="button"
        >
          Reset Filters
        </button>
      </div>
    </div>
  );
}

// ========== PHONEPE-STYLE SIDE TRANSITION PANEL ==========
function getTxnTypeInfo(type: TransactionType) {
  if (type === "buy")
    return {
      color: "bg-[#e6fbe9] text-[#23be5a]",
      icon: <BsArrowDownCircle className="text-[#47bc6b] text-2xl" />,
      label: "Buy / Invest",
    };
  if (type === "sell")
    return {
      color: "bg-[#ffe3ea] text-[#e23a4e]",
      icon: <BsArrowUpCircle className="text-[#ef5398] text-2xl" />,
      label: "Sell",
    };
  return {
    color: "bg-[#fefee8] text-[#c6a01d]",
    icon: <HiOutlineCurrencyRupee className="text-[#d6b01c] text-2xl" />,
    label: "Withdraw",
  };
}

// PhonePe-like vertical side panel -- now on the RIGHT SIDE
function PhonePeSidePanel({
  transactions,
  allStocks,
  allParties,
  categories,
  onSelect,
  selectedTxnId,
}: {
  transactions: Transaction[];
  allStocks: Stock[];
  allParties: Party[];
  categories: Category[];
  onSelect: (txn: {
    transaction: Transaction;
    stock: Stock | undefined;
    category: Category | any;
    party: Party | undefined;
  }) => void;
  selectedTxnId: string | number | null;
}) {
  // Scroll to selected transaction
  React.useEffect(() => {
    if (!selectedTxnId) return;
    const el = document.getElementById("txn-item-" + selectedTxnId);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selectedTxnId]);

  // Get linked objects
  function findDetails(t: Transaction) {
    const stock = allStocks.find((s) => String(s.id) === String(t.stock_id));
    let category = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => {
        const catId = typeof cat.id === "object" ? undefined : cat.id;
        const catName =
          typeof cat.category_name === "object"
            ? undefined
            : cat.category_name;
        const catValue = typeof cat === "object" ? undefined : cat;
        return String(catId ?? catName ?? catValue) === String(t.category_id);
      });
    }
    if (!category && t.category_id) {
      category = categories.find(
        (c) =>
          (typeof c.id !== "object" &&
            String(c.id) === String(t.category_id)) ||
          (typeof c.category_name !== "object" &&
            c.category_name === t.category_id)
      );
    }
    const party = allParties.find(
      (p) => String(p.id) === String(t.party_id)
    );
    return { stock, category, party };
  }

  return (
    <aside
      className="fixed top-0 right-0 h-full bg-white border-l z-40 shadow-2xl flex flex-col px-0 pt-3 pb-3 w-[350px] max-w-full min-w-[238px] transition-all duration-300 animate-fade-in overflow-y-auto"
      style={{
        boxShadow: "-2px 0 18px -3px #b0cbf533",
        background:
          "linear-gradient(180deg,#e8f0fe 3%,#fcfcff 90%)",
      }}
    >
      <div className="text-lg px-6 pb-1.5 font-black text-[#25669a] flex gap-2 items-center sticky top-0 bg-white z-10">
        <FaExchangeAlt className="text-[#357fb2]" />
        Transactions ({transactions.length})
      </div>
      <div className="flex-1 overflow-y-auto pr-3 pt-3 pb-3">
        <ul className="flex flex-col gap-2">
          {transactions.map((txn) => {
            const { color, icon, label } = getTxnTypeInfo(txn.type);
            const { stock, category, party } = findDetails(txn);
            return (
              <li
                id={`txn-item-${txn.id}`}
                key={txn.id}
                className={`rounded-xl border border-[#e0e6f9] px-3 py-2 mb-1 cursor-pointer flex gap-2 items-center shadow font-sans transition-all duration-200
                  ${selectedTxnId === txn.id ? "ring-2 ring-[#8edbfc] bg-[#eafdff]" : "hover:bg-[#fbf7fe]"}`}
                onClick={() => onSelect({ transaction: txn, stock, category, party })}
                tabIndex={0}
              >
                <span className={"rounded-xl flex items-center justify-center w-10 h-10 mr-2 font-bold shadow-sm border " + color}>
                  {icon}
                </span>
                <div className="flex-1 flex flex-col gap-[2px]">
                  <div className="text-[16px] font-bold leading-[1.20] select-all text-[#2b4254]">
                    {typeof party?.name === "object" ? "-" : party?.name ?? party?.id ?? "-"}
                  </div>
                  <div className="text-[11.5px] text-[#879ab3] font-semibold tracking-wide">
                    {txn.date} &bull; {label}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#d4f7fd]/60 text-[#257a9e]">
                      <BsBoxArrowInDownRight className="inline w-4 h-4" />
                      {typeof stock?.id === "object" ? "-" : stock?.id ?? txn.stock_id ?? "-"}
                    </span>
                    <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#dbe1f5] text-[#6759c9]">
                      {typeof category?.category_name === "object"
                        ? "-"
                        : category?.category_name ||
                          (typeof category?.category === "object"
                            ? "-"
                            : category?.category) ||
                          (typeof category?.stock_name === "object"
                            ? "-"
                            : category?.stock_name) ||
                          (typeof txn.category_id === "object"
                            ? "-"
                            : txn.category_id) ||
                          "-"}
                    </span>
                    <span
                      className={`text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#def1e7] text-[#12876a]`}
                    >
                      x{txn.qty} {typeof txn.unit === "object" ? "-" : txn.unit ?? "-"}
                    </span>
                  </div>
                </div>
                <div className="ml-1 text-right flex flex-col justify-between h-full">
                  <span
                    className={
                      "font-bold text-[15px] " +
                      (txn.type === "buy"
                        ? "text-[#12b386]"
                        : txn.type === "sell"
                        ? "text-[#f24464]"
                        : "text-[#ba961b]")
                    }
                  >
                    {formatMoney(txn.amount, { symbol: true })}
                  </span>
                  <span className="text-[9.5px] text-[#a8abb6] font-medium block mt-1 text-right">
                    {txn.remark && typeof txn.remark !== "object" ? txn.remark : ""}
                  </span>
                </div>
              </li>
            );
          })}
          {!transactions.length && (
            <li className="text-center text-[#b3b9cf] font-semibold py-12">No transactions.</li>
          )}
        </ul>
      </div>
      <span className="block px-6 py-3 text-xs text-[#6a8cbc] font-semibold border-t bg-[#e7f7ff]">
        <span className="font-bold text-[#3e7bbd]">Tip:</span> Tap a transaction to view more details quickly.
      </span>
    </aside>
  );
}

// ========== MAIN COMPONENT ==========
function UserHistory() {
  const [allParties, setAllParties] = useState<Party[]>([]);
  const [allStocks, setAllStocks] = useState<Stock[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [stockHistory, setStockHistory] = useState<StockHistory[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [sellTxnsRaw, setSellTxnsRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterState>({});
  const [isLoading, setIsLoading] = useState(true);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(true);

  useEffect(() => {
    if (window.location.hash.startsWith("#q=")) {
      const quickKey = decodeURIComponent(window.location.hash.slice(3));
      if (QUICK_FILTERS.find(q => q.key === quickKey)) {
        setFilters((prev) => ({ ...prev, quick: quickKey, ...applyQuickFilter(quickKey) }));
      }
    }
    // eslint-disable-next-line
  }, []);

  // Side panel selection state
  const [selectedTxnPanel, setSelectedTxnPanel] = useState<string | number | null>(null);

  const [selectedTransaction, setSelectedTransaction] = useState<{
    transaction: Transaction;
    stock: Stock | undefined;
    category: Category | any;
    party: Party | undefined;
  } | null>(null);

  useEffect(() => {
    async function fetchAll() {
      setIsLoading(true);
      try {
        const partiesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/addparty`
        );
        setAllParties(partiesRes.data || []);

        const stocksRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/addstocks`
        );
        let stocksArr = Array.isArray(stocksRes.data) ? stocksRes.data : [];
        stocksArr = stocksArr.map((stock: any) => ({
          ...stock,
          categories: (() => {
            try {
              if (Array.isArray(stock.categories)) return stock.categories;
              if (
                typeof stock.categories === "string" &&
                stock.categories.length > 0
              )
                return JSON.parse(stock.categories);
              return [];
            } catch {
              return [];
            }
          })(),
        }));
        setAllStocks(stocksArr);

        const withdrawalRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/withdrawals`
        );
        setWithdrawals(withdrawalRes.data || []);

        const stockHistoryRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/updatestock`
        );
        setStockHistory(stockHistoryRes.data || []);

        const categoriesRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/categories`
        );
        setCategories(categoriesRes.data || []);

        // --- SELL STOCKS API ---
        const sellStocksRes = await axios.get(
          `https://stockaadvikbackend.aadviktech.com/auth/sellstocks`
        );
        setSellTxnsRaw(Array.isArray(sellStocksRes.data) ? sellStocksRes.data : []);

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchAll();
  }, []);

  function handleQuickFilter(key: string) {
    setFilters((prev) => ({
      ...prev,
      ...applyQuickFilter(key),
      quick: key,
    }));
    window.location.hash = "#q=" + encodeURIComponent(key);
  }
  function clearQuickFilter() {
    setFilters((prev) => {
      let { quick, ...rest } = prev;
      return { ...rest };
    });
    window.location.hash = "";
  }

  let buyTransactions: Transaction[] = [];
  allStocks.forEach((stock) => {
    if (Array.isArray(stock.categories) && stock.categories.length > 0) {
      stock.categories.forEach((cat: any) => {
        const totalAmount =
          typeof cat.total_amount === "object"
            ? 0
            : Number(cat.total_amount) || 0;
        const unitValue =
          typeof cat.unit === "object" ? "" : cat.unit ?? "";
        buyTransactions.push({
          id: `buy_${stock.id}_${cat.id ?? cat.category_name}`,
          party_id: stock.party_id ?? "",
          date: stock.date?.slice(0, 10) || "",
          type: "buy",
          stock_id: stock.id,
          category_id: cat.id ?? (cat.category_name || cat),
          qty: typeof cat.unit === "object" ? 1 : Number(cat.unit) || 1,
          unit: unitValue,
          amount: totalAmount,
          remark: "Stock added",
        });
      });
    }
  });

  let withdrawalTransactions: Transaction[] = [];
  withdrawals.forEach((wd) => {
    let cats: any[] = [];
    try {
      cats = JSON.parse(wd.catrgories || "[]");
      if (!Array.isArray(cats)) cats = [];
    } catch {
      cats = [];
    }
    if (cats.length === 0) {
      withdrawalTransactions.push({
        id: `withdraw_${wd.id}`,
        party_id: wd.party_id,
        date: wd.date?.slice(0, 10) || "",
        type: "withdraw",
        stock_id: undefined,
        category_id: undefined,
        qty: 1,
        amount: typeof wd.amount === "object" ? 0 : Number(wd.amount),
        remark: "Withdrawal",
      });
      return;
    }
    cats.forEach((c, i) => {
      let catId;
      if (typeof c === "object" && c !== null) {
        if (
          typeof c.id === "object" ||
          typeof c.category_name === "object"
        ) {
          catId = "-";
        } else {
          catId = c.id ?? c.category_name ?? String(c);
        }
      } else {
        catId = c;
      }
      withdrawalTransactions.push({
        id: `withdraw_${wd.id}_${i}`,
        party_id: wd.party_id,
        date: wd.date?.slice(0, 10) || "",
        type: "withdraw",
        stock_id: undefined,
        category_id: catId,
        qty: 1,
        amount:
          typeof wd.amount === "object" ? 0 : Number(wd.amount) / cats.length,
        remark: "Withdrawal",
      });
    });
  });

  // Map sellTxnsRaw to Transaction[] with compatible fields
  const sellTransactions: Transaction[] = useMemo(() => {
    return (sellTxnsRaw || []).map((sell) => ({
      id: `sell_${sell.id}`,
      party_id: sell.party_id,
      date: sell.date?.slice(0, 10) ?? "",
      type: "sell",
      stock_id: sell.stock_id,
      category_id: sell.category, // sell.category is string
      qty: typeof sell.sell_units === "string"
        ? parseFloat(sell.sell_units)
        : (typeof sell.sell_units === "number" ? sell.sell_units : 0),
      unit: "units",
      amount: typeof sell.sell_amount === "string"
        ? parseFloat(sell.sell_amount)
        : (typeof sell.sell_amount === "number" ? sell.sell_amount : 0),
      remark: "Sell",
      indirect_charges: sell.indirect_charges
        ? Number(sell.indirect_charges)
        : undefined,
      // Optionally stock_name: sell.stock_name
    }));
  }, [sellTxnsRaw]);

  let allTransactionsRaw: Transaction[] = [
    ...buyTransactions,
    ...withdrawalTransactions,
    ...sellTransactions,
  ].map((t) => ({
    ...t,
    unit: typeof t.unit === "object" ? "-" : t.unit || "-",
  }));

  const filteredTransactions = useMemo(() => {
    return allTransactionsRaw
      .filter((t) => {
        if (filters.party_id && String(t.party_id) !== String(filters.party_id))
          return false;
        if (filters.from && t.date < filters.from) return false;
        if (filters.to && t.date > filters.to) return false;
        if (filters.type && filters.type !== "All" && t.type !== filters.type)
          return false;
        if (
          filters.stock_id &&
          filters.stock_id !== "All" &&
          String(t.stock_id) !== String(filters.stock_id)
        )
          return false;
        if (
          filters.category_id &&
          filters.category_id !== "All" &&
          String(t.category_id) !== String(filters.category_id)
        )
          return false;
        if (
          filters.minAmount &&
          (isNaN(Number(t.amount)) ||
            Number(t.amount) < Number(filters.minAmount))
        )
          return false;
        if (
          filters.maxAmount &&
          (isNaN(Number(t.amount)) ||
            Number(t.amount) > Number(filters.maxAmount))
        )
          return false;
        if (
          filters.search &&
          !(
            String(t.id).includes(filters.search) ||
            String(t.remark || "")
              .toLowerCase()
              .includes(filters.search.toLowerCase()) ||
            String(t.amount).includes(filters.search) ||
            String(t.unit).includes(filters.search)
          )
        )
          return false;
        return true;
      })
      .sort((a, b) =>
        a.date === b.date
          ? String(a.id) < String(b.id)
            ? -1
            : 1
          : new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    // eslint-disable-next-line
  }, [
    JSON.stringify(allTransactionsRaw),
    filters.from,
    filters.to,
    filters.type,
    filters.stock_id,
    filters.category_id,
    filters.party_id,
    filters.minAmount,
    filters.maxAmount,
    filters.search,
  ]);

  const uniqueStocks = Array.from(
    new Map(allStocks.map((s) => [String(s.id), s])).values()
  );
  const uniqueCategories = Array.from(
    new Set(
      allStocks.flatMap((s) =>
        Array.isArray(s.categories)
          ? s.categories.map((cat) =>
              typeof cat.category_name === "object"
                ? undefined
                : cat.category_name ??
                  (typeof cat.category === "object"
                    ? undefined
                    : cat.category) ??
                  (typeof cat.id === "object" ? undefined : cat.id)
            )
          : []
      )
    )
  )
    .filter(Boolean)
    .map((id) => ({ id, label: id }));

  function findStockAndCategory(
    stock_id: string | number | undefined,
    category_id: string | number | undefined
  ) {
    const stock = allStocks.find((s) => String(s.id) === String(stock_id));
    let category = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => {
        const catId = typeof cat.id === "object" ? undefined : cat.id;
        const catName =
          typeof cat.category_name === "object"
            ? undefined
            : cat.category_name;
        const catValue = typeof cat === "object" ? undefined : cat;
        return String(catId ?? catName ?? catValue) === String(category_id);
      });
    }
    if (!category && category_id) {
      category = categories.find(
        (c) =>
          (typeof c.id !== "object" &&
            String(c.id) === String(category_id)) ||
          (typeof c.category_name !== "object" &&
            c.category_name === category_id)
      );
    }
    // If not found, for sell txns, use category string as fallback
    if (!category && category_id) {
      category = { category_name: category_id }; // fallback stub
    }
    return { stock, category };
  }

  let totalInvested = 0,
    totalWithdrawn = 0,
    totalSell = 0,
    totalBalance = 0;

  type AssetHolding = {
    stock_id: string | number | undefined;
    category_id: string | number | undefined;
    party_id: number | string;
    stock?: Stock;
    category?: any;
    total_bought: number;
    total_withdraw: number;
    total_sold: number;
    remaining_qty: number;
    unit: string;
    current_price: number;
    holding_value: number;
    history: Transaction[];
    avg_buy_price?: number;
  };
  const assetHoldingsMap: Map<string, AssetHolding> = new Map();

  filteredTransactions.forEach((t) => {
    const key = `${t.stock_id || "-"}_${t.category_id || "-"}`;
    if (!assetHoldingsMap.has(key)) {
      const { stock, category } = findStockAndCategory(
        t.stock_id,
        t.category_id
      );
      assetHoldingsMap.set(key, {
        stock_id: t.stock_id,
        category_id: t.category_id,
        party_id: t.party_id,
        stock,
        category,
        total_bought: 0,
        total_withdraw: 0,
        total_sold: 0,
        remaining_qty: 0,
        unit: typeof t.unit === "object" ? "-" : t.unit || "-",
        current_price: 0,
        holding_value: 0,
        history: [],
      });
    }
    const record = assetHoldingsMap.get(key)!;
    if (t.type === "buy") {
      record.total_bought += t.qty;
      record.remaining_qty += t.qty;
      totalInvested += t.amount;
    }
    if (t.type === "withdraw") {
      record.total_withdraw += t.qty;
      record.remaining_qty -= t.qty;
      totalWithdrawn += t.amount;
    }
    if (t.type === "sell") {
      record.total_sold += t.qty;
      record.remaining_qty -= t.qty;
      totalSell += t.amount;
    }
    record.history.push(t);
  });

  assetHoldingsMap.forEach((record) => {
    let priceRec = stockHistory
      .filter(
        (r) =>
          String(r.stock_id) === String(record.stock_id) &&
          String(r.cat_id ?? "") === String(record.category_id ?? "")
      )
      .sort(
        (a, b) =>
          new Date(b.current_price_date).getTime() -
          new Date(a.current_price_date).getTime()
      )[0];
    let cprice = 0;
    if (priceRec && priceRec.current_price) {
      cprice =
        typeof priceRec.current_price === "string"
          ? parseFloat(priceRec.current_price)
          : priceRec.current_price;
    } else if (
      record.category &&
      typeof record.category.total_amount !== "undefined" &&
      typeof record.category.unit !== "undefined" &&
      typeof record.category.total_amount !== "object" &&
      typeof record.category.unit !== "object"
    ) {
      cprice =
        Number(record.category.total_amount) /
        (Number(record.category.unit) || 1);
    }
    record.current_price = cprice || 0;
    record.holding_value = (record.remaining_qty || 0) * (cprice || 0);
    const buys = record.history.filter(tr => tr.type === "buy");
    const totalBuysValue = buys.reduce((sum, tr) => sum + (tr.amount ?? 0), 0);
    const totalQty = buys.reduce((sum, tr) => sum + (tr.qty ?? 0), 0);
    record.avg_buy_price = totalQty ? totalBuysValue / totalQty : 0;
    if (record.holding_value > 0) totalBalance += record.holding_value;
  });

  const percentReturn = totalInvested
    ? (((totalBalance + totalWithdrawn + totalSell - totalInvested) / totalInvested) * 100).toFixed(2)
    : "0.00";
  const statDetails = [
    {
      icon: <IoWalletSharp className="text-[#F49A40] text-2xl" />,
      label: "Total Invested",
      value: totalInvested,
      color: "#fffee0",
      sub: "All buy amount.",
    },
    {
      icon: <FaRegArrowAltCircleDown className="text-[#29b87f] text-2xl" />,
      label: "Withdrawn",
      value: totalWithdrawn,
      color: "#e7fbe8",
      sub: "All withdrawals.",
    },
    {
      icon: <FaRegArrowAltCircleUp className="text-[#ed0e25] text-2xl" />,
      label: "Amount Sold",
      value: totalSell,
      color: "#ffe3e7",
      sub: "Sells (from sellstocks API).",
    },
    {
      icon: <GiWallet className="text-[#319F68] text-2xl" />,
      label: "Current Value",
      value: totalBalance,
      color: "#e1efed",
      sub: "Current value of holdings.",
    },
    {
      icon: <MdOutlineInsights className="text-[#8b86e3] text-2xl" />,
      label: "ROI",
      value: `${percentReturn}%`,
      color: "#f7f3ff",
      sub: "Total Return on Investment",
    },
  ];

  const TRANSACTION_TYPE_OPTIONS = [
    { value: "All", label: "All Types" },
    { value: "buy", label: "Buy" },
    { value: "withdraw", label: "Withdraw" },
    { value: "sell", label: "Sell" },
  ];

  function printPage() {
    window.print();
  }
  function getYearlySummary() {
    const summary: { [year: string]: number } = {};
    filteredTransactions.forEach(t => {
      const year = t.date?.slice(0, 4) ?? "";
      if (year) summary[year] = (summary[year] || 0) + (t.amount ?? 0);
    });
    return summary;
  }

  const barData = {
    labels: Array.from(assetHoldingsMap.values()).map((a) =>
      typeof a.stock?.id === "object" ? "-" : a.stock?.id || a.stock_id
    ),
    datasets: [
      {
        label: "Holding Value",
        data: Array.from(assetHoldingsMap.values()).map((a) =>
          typeof a.holding_value === "object" ? 0 : a.holding_value
        ),
        backgroundColor: "#74C0FC",
      },
    ],
  };

  const [visibleCols, setVisibleCols] = useState({
    date: true,
    stock: true,
    category: true,
    party: true,
    type: true,
    qty: true,
    unit: true,
    amount: true,
    remark: true,
    avg_buy_price: false,
  });

  const tableSum = useMemo(() => {
    return filteredTransactions.reduce(
      (acc, curr) => {
        acc.qty += curr.qty;
        acc.amount += curr.amount ?? 0;
        return acc;
      },
      { qty: 0, amount: 0 }
    );
    // eslint-disable-next-line
  }, [filteredTransactions]);

  // ========== RENDER ==========
  return (
    <div className="bg-gradient-to-bl from-[#e8f0fe] to-[#eaefec] min-h-screen flex flex-col font-sans relative animate-fade-in">
      {/* PhonePe-Style Side Transaction Panel RIGHT */}
      
      {/* Header - add margin-right for sidepanel (removed ml-*) */}
      <header className="w-full flex justify-between items-center px-8 py-5 border-b bg-white shadow-sm sticky top-0 z-40 mr-[350px]">
        <div className="text-2xl tracking-tight font-bold text-[#274472] flex items-center gap-3">
          <AiOutlineFundProjectionScreen className="text-blue-400" /> Asset Investment Tracker
        </div>
        <div className="flex gap-2 md:gap-3">
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Download as CSV"
            onClick={() => downloadCSV(filteredTransactions, allParties, "csv")}
          >
            <FaDownload className="text-base" />
            Export CSV
          </button>
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Download for Excel"
            onClick={() => downloadCSV(filteredTransactions, allParties, "excel")}
          >
            <FaDownload className="text-base" />
            Excel
          </button>
          <button
            className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 transition border text-[#333] shadow"
            title="Print"
            onClick={printPage}
          >
            <MdPrint className="text-lg" /> Print
          </button>
        </div>
      </header>
      <div className="flex relative">
        {/* Side Panel: Transaction List with expand/collapse */}
        <aside
          className={`fixed top-0 right-0 h-full bg-white shadow-lg z-40 transition-all duration-300 flex flex-col
            ${sidePanelCollapsed ? "w-14 mr-0" : "w-[350px] mr-0"}`}
          style={{ minWidth: sidePanelCollapsed ? 56 : 350 }}
        >
          {/* Expand/Collapse Button */}
          <button
            className={`absolute top-4 left-0 -translate-x-full bg-[#e4eaff] hover:bg-[#d2dbfc] 
              text-[#4175EB] border border-[#cfdafe] rounded-r-lg shadow px-2 py-1 transition-all duration-200 z-50`}
            style={{ outline: "none" }}
            onClick={() => setSidePanelCollapsed(v => !v)}
            tabIndex={0}
            aria-label={sidePanelCollapsed ? "Expand transactions panel" : "Collapse transactions panel"}
          >
            {sidePanelCollapsed ? (
              <span className="flex items-center">
                <svg className="h-6 w-6" fill="none" stroke="#4466e7" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
                </svg>
              </span>
            ) : (
              <span className="flex items-center">
                <svg className="h-6 w-6" fill="none" stroke="#4466e7" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"></path>
                </svg>
              </span>
            )}
          </button>
          {/* Only show content if expanded */}
          {!sidePanelCollapsed && (
            <div className="flex-1 overflow-y-auto">
              <PhonePeSidePanel
                transactions={filteredTransactions}
                allStocks={allStocks}
                allParties={allParties}
                categories={categories}
                onSelect={({ transaction, stock, category, party }) => {
                  setSelectedTransaction({ transaction, stock, category, party });
                  setSelectedTxnPanel(transaction.id);
                }}
                selectedTxnId={selectedTxnPanel}
              />
            </div>
          )}
        </aside>
        {/* Main Content */}
        <main className={`flex-1 w-full px-4 sm:px-10 lg:px-16 py-3 grid gap-7 transition-all duration-300 ${sidePanelCollapsed ? "mr-14" : "mr-[350px]"}`}>
          {/* QUICK FILTERS */}
          <section className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[13px] text-[#4897ad] font-semibold mr-2 flex items-center gap-1">
              <TbHistory /> Quick Filters:
            </span>
            {QUICK_FILTERS.map(q => (
              <button
                key={q.key}
                onClick={() => handleQuickFilter(q.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition 
                  ${filters.quick === q.key ? "bg-[#63dbff]/20 border-[#3296bd] text-[#197995]" : 
                  "bg-[#f4fafd] border-[#d3e4f2] text-[#558cb3] hover:bg-[#e6f4ff]"
                  }`}
              >
                {q.label}
              </button>
            ))}
            {filters.quick && (
              <button
                className="ml-2 px-2 py-1 rounded bg-[#fffa] text-[#888] border border-[#ecd5ff] hover:bg-[#fde6ff] text-xs font-semibold"
                onClick={clearQuickFilter}
              >Clear Filter</button>
            )}
          </section>
          {/* ADVANCED FILTERS */}
          <AdvancedFilters
            filters={filters}
            setFilters={setFilters}
            allParties={allParties}
            allStocks={allStocks}
            allCategories={categories}
          />
          {/* STAT CARDS */}
          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {statDetails.map((d, idx) => (
              <div
                key={idx}
                className="rounded-xl flex flex-col gap-3 px-6 py-6 shadow relative z-0 border bg-gradient-to-br from-white to-[#f6fafb]"
                style={{
                  borderColor: "#E3E8EE",
                  background: d.color,
                  minHeight: 130,
                  justifyContent: "center",
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-white shadow rounded-full w-11 h-11 flex justify-center items-center border">
                    {d.icon}
                  </div>
                  <div>
                    <div className="font-bold text-lg text-[#18263a]">
                      {typeof d.value === "string"
                        ? d.value
                        : formatMoney(d.value, { symbol: true })}
                    </div>
                    <div className="text-xs font-semibold text-[#758ba7]">
                      {d.label}
                    </div>
                    <div className="text-[10px] text-[#b4bfce]">{d.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </section>
          {/* Holdings Table */}
          <section className="bg-white rounded-xl shadow p-6 mb-10">
            <div className="font-bold text-[#3F4765] text-xl mb-6 flex items-center gap-2">
              <GiWallet className="text-[#4883e9]/60" /> Current Asset Holdings
              <span className="text-xs ml-2 text-[#7a9ac3]">
                ({assetHoldingsMap.size} items)
              </span>
            </div>
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-[900px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#F7F8FA] border-b">
                    <th className="p-2 text-[#3b4d6f] text-xs font-bold">Stock</th>
                    <th className="p-2 text-[#3b4d6f] text-xs font-bold">Category</th>
                    <th className="p-2 font-bold">Total Bought</th>
                    <th className="p-2 font-bold">Withdrawn</th>
                    <th className="p-2 font-bold">Sold</th>
                    <th className="p-2 font-bold">Remaining</th>
                    <th className="p-2 font-bold">Current Price</th>
                    <th className="p-2 font-bold">Avg Buy Price</th>
                    <th className="p-2 font-bold">Holding Value</th>
                    <th className="p-2 font-bold">Trend Graph</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(assetHoldingsMap.values()).map((a, idx) => (
                    <tr
                      key={`${a.stock_id}_${a.category_id}`}
                      className={idx % 2 === 0 ? "bg-[#f6fafb]" : "bg-white"}
                    >
                      <td className="p-2">
                        {a.stock_id ? (
                          <Link
                            className="underline text-[#226C92] font-semibold"
                            to={`/stocks/${a.stock_id}`}
                          >
                            {typeof a.stock?.id === "object"
                              ? "-"
                              : a.stock?.id || a.stock_id}
                          </Link>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-2">
                        {typeof a.category?.category_name === "object"
                          ? "-"
                          : a.category?.category_name ||
                            (typeof a.category?.category === "object"
                              ? "-"
                              : a.category?.category) ||
                            (typeof a.category?.stock_name === "object"
                              ? "-"
                              : a.category?.stock_name) ||
                            (typeof a.category_id === "object"
                              ? "-"
                              : a.category_id) ||
                            "-"}
                      </td>
                      <td className="p-2 font-bold text-[#16a34a]">
                        {a.total_bought}{" "}
                        {typeof a.unit === "object" ? "-" : a.unit}
                      </td>
                      <td className="p-2 font-bold text-[#fbbf24]">
                        {a.total_withdraw}{" "}
                        {typeof a.unit === "object" ? "-" : a.unit}
                      </td>
                      <td className="p-2 font-bold text-[#ef4444]">
                        {a.total_sold} {typeof a.unit === "object" ? "-" : a.unit}
                      </td>
                      <td className="p-2 font-semibold text-[#11977D]">
                        {a.remaining_qty}{" "}
                        {typeof a.unit === "object" ? "-" : a.unit}
                      </td>
                      <td className="p-2 font-normal">
                        {formatMoney(a.current_price, { symbol: true })}
                      </td>
                      <td className="p-2 font-normal text-[#5751d6]">
                        {formatMoney(a.avg_buy_price ?? 0, { symbol: true })}
                      </td>
                      <td className="p-2 font-bold text-[#1457f1]">
                        {formatMoney(a.holding_value, { symbol: true })}
                      </td>
                      <td>
                        <div style={{ width: 68, height: 24 }}>
                          <Line
                            data={{
                              labels: a.history.map((tr, i) => i + 1),
                              datasets: [
                                {
                                  label: "Qty",
                                  data: a.history.map((tr) => tr.qty),
                                  backgroundColor: "#93DEFF99",
                                  borderColor: "#19a0fa",
                                  pointRadius: 0,
                                  tension: 0.5,
                                },
                              ],
                            }}
                            options={{
                              responsive: false,
                              plugins: { legend: { display: false } },
                              scales: {
                                x: { display: false },
                                y: { display: false },
                              },
                              elements: {
                                line: { borderWidth: 2 },
                                point: { radius: 1 },
                              },
                            }}
                            height={24}
                            width={68}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {assetHoldingsMap.size === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="p-4 text-center text-[#9CA3AF]"
                      >
                        No asset holdings found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          {/* Ledger / Transaction Table */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-[#3F4765] text-xl flex items-center gap-2">
                <FaExchangeAlt className="text-[#357fb2]" /> All Transactions Ledger
                <span className="text-xs text-[#7a9ac3]">
                  ({filteredTransactions.length} entries)
                </span>
              </span>
              <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-1 text-xs font-semibold text-[#3F4765]">
                  Columns:
                  {Object.keys(visibleCols).map((col) => (
                    <span key={col} className="flex items-center gap-1 ml-2">
                      <input
                        type="checkbox"
                        checked={visibleCols[col as keyof typeof visibleCols]}
                        onChange={() =>
                          setVisibleCols((prev) => ({
                            ...prev,
                            [col]: !prev[col as keyof typeof visibleCols],
                          }))
                        }
                      />{" "}
                      {col
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, ch => ch.toUpperCase())}
                    </span>
                  ))}
                </label>
              </div>
            </div>
            <div className="rounded overflow-x-auto max-w-full">
              <table className="min-w-[950px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#f3f7fa] border-b text-[#42547b]">
                    {visibleCols.date && <th className="p-2">Date</th>}
                    {visibleCols.stock && <th className="p-2">Stock</th>}
                    {visibleCols.category && <th className="p-2">Category</th>}
                    {visibleCols.party && <th className="p-2">Party</th>}
                    {visibleCols.type && <th className="p-2">Type</th>}
                    {visibleCols.qty && <th className="p-2">Qty</th>}
                    {visibleCols.unit && <th className="p-2">Unit</th>}
                    {visibleCols.amount && (
                      <th className="p-2">Amount (₹)</th>
                    )}
                    {visibleCols.avg_buy_price && (
                      <th className="p-2">Avg Buy Price</th>
                    )}
                    {visibleCols.remark && <th className="p-2">Remark</th>}
                    <th className="p-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length ? (
                    filteredTransactions.map((t, idx) => {
                      const { stock, category } = findStockAndCategory(
                        t.stock_id,
                        t.category_id
                      );
                      const party = allParties.find(
                        (p) => String(p.id) === String(t.party_id)
                      );
                      let avgBuyPrice = undefined;
                      if (visibleCols.avg_buy_price) {
                        const ah = assetHoldingsMap.get(`${t.stock_id || "-"}_${t.category_id || "-"}`);
                        avgBuyPrice = ah?.avg_buy_price ?? undefined;
                      }
                      return (
                        <tr
                          key={t.id}
                          className={
                            idx % 2 === 0 ? "bg-[#ecf7fb]" : "bg-[#fbfbfd]"
                          }
                        >
                          {visibleCols.date && (
                            <td className="p-2 whitespace-nowrap">
                              {t.date}
                            </td>
                          )}
                          {visibleCols.stock && (
                            <td className="p-2">
                              {typeof stock?.id === "object"
                                ? "-"
                                : stock?.id ??
                                  (typeof t.stock_id === "object"
                                    ? "-"
                                    : t.stock_id) ??
                                  "-"}
                            </td>
                          )}
                          {visibleCols.category && (
                            <td className="p-2">
                              {typeof category?.category_name === "object"
                                ? "-"
                                : category?.category_name ||
                                  (typeof category?.category === "object"
                                    ? "-"
                                    : category?.category) ||
                                  (typeof category?.stock_name === "object"
                                    ? "-"
                                    : category?.stock_name) ||
                                  (typeof t.category_id === "object"
                                    ? "-"
                                    : t.category_id) ||
                                  "-"}
                            </td>
                          )}
                          {visibleCols.party && (
                            <td className="p-2">
                              {typeof party?.name === "object"
                                ? "-"
                                : party?.name ??
                                  (typeof t.party_id === "object"
                                    ? "-"
                                    : t.party_id) ??
                                  "-"}
                            </td>
                          )}
                          {visibleCols.type && (
                            <td className="p-2">
                              {t.type === "buy" && (
                                <span className="inline-flex items-center gap-1 text-[#36a859] font-bold">
                                  <BsBoxArrowInDown /> Buy
                                </span>
                              )}
                              {t.type === "sell" && (
                                <span className="inline-flex items-center gap-1 text-[#F04959] font-bold">
                                  <BsBoxArrowUpRight /> Sell
                                </span>
                              )}
                              {t.type === "withdraw" && (
                                <span className="inline-flex items-center gap-1 text-[#c87b12] font-bold">
                                  <TbZoomMoney /> Withdraw
                                </span>
                              )}
                            </td>
                          )}
                          {visibleCols.qty && (
                            <td className="p-2">{t.qty}</td>
                          )}
                          {visibleCols.unit && (
                            <td className="p-2">
                              {typeof t.unit === "object"
                                ? "-"
                                : t.unit ?? "-"}
                            </td>
                          )}
                          {visibleCols.amount && (
                            <td className="p-2 font-semibold text-[#336995]">
                              {typeof t.amount === "object"
                                ? "0.00"
                                : t.amount
                                ? Number(t.amount).toLocaleString("en-IN", {
                                    maximumFractionDigits: 2,
                                  })
                                : "0.00"}
                            </td>
                          )}
                          {visibleCols.avg_buy_price && (
                            <td className="p-2 font-semibold text-[#856cdb]">{avgBuyPrice ? formatMoney(avgBuyPrice, { symbol: true }) : "-"}</td>
                          )}
                          {visibleCols.remark && (
                            <td className="p-2 text-[#717E98]">
                              {typeof t.remark === "object"
                                ? "-"
                                : t.remark ?? "-"}
                            </td>
                          )}
                          <td className="p-2 text-center">
                            <button
                              className="text-blue-500 hover:underline text-xs font-semibold"
                              title="View Details"
                              onClick={() => {
                                setSelectedTransaction({
                                  transaction: t,
                                  stock,
                                  category,
                                  party,
                                });
                                setSelectedTxnPanel(t.id);
                              }}
                            >
                              Detail
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          Object.values(visibleCols).filter((x) => x).length + 1
                        }
                        className="p-6 text-center text-[#B1B5C4] text-sm"
                      >
                        No transaction found
                      </td>
                    </tr>
                  )}
                </tbody>
                {filteredTransactions.length > 0 && (
                  <tfoot>
                    <tr className="bg-[#ecf7fb] font-bold text-[#436095] border-t">
                      {visibleCols.date && <td className="p-2 text-right" colSpan={1}>Total</td>}
                      {visibleCols.stock && <td></td>}
                      {visibleCols.category && <td></td>}
                      {visibleCols.party && <td></td>}
                      {visibleCols.type && <td></td>}
                      {visibleCols.qty && (
                        <td className="p-2">{tableSum.qty}</td>
                      )}
                      {visibleCols.unit && <td></td>}
                      {visibleCols.amount && (
                        <td className="p-2 text-[#218758]">{formatMoney(tableSum.amount, { symbol: true })}</td>
                      )}
                      {visibleCols.avg_buy_price && <td></td>}
                      {visibleCols.remark && <td></td>}
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>
          {/* VISUALS */}
          <section className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-8 mb-4 animate-fade-in">
            {/* Pie: Category */}
            <div className="relative bg-gradient-to-tl from-[#fff3ec] via-[#e4efff] to-[#f1f8ff] shadow-xl rounded-3xl p-8 flex flex-col items-center col-span-2 border-2 border-[#e9e9fc] hover:scale-[1.03] transition-all duration-200 group overflow-hidden min-h-[350px]">
              <span className="absolute -top-10 -left-12 w-[170px] h-[170px] bg-gradient-to-tr from-[#b7acf1] via-[#74daf8] to-[#fff3ec] opacity-10 rounded-full pointer-events-none"></span>
              <div className="mb-6 relative z-10 flex items-center gap-2">
                <FaChartPie className="w-7 h-7 text-[#8357ff] drop-shadow-md" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-r from-[#633fef] to-[#7ae883] tracking-wide drop-shadow-lg">Holdings by Category</span>
              </div>
              <div className="w-[220px] h-[220px] flex items-center justify-center relative z-10">
                <Doughnut
                  data={{
                    labels: Array.from(assetHoldingsMap.values()).map((a) =>
                      typeof a.category?.category_name === "object"
                        ? "-"
                        : a.category?.category_name ||
                          (typeof a.category?.category === "object"
                            ? "-"
                            : a.category?.category) ||
                          (typeof a.category?.stock_name === "object"
                            ? "-"
                            : a.category?.stock_name) ||
                          (typeof a.category_id === "object"
                            ? "-"
                            : a.category_id) ||
                          "-"
                    ),
                    datasets: [
                      {
                        data: Array.from(assetHoldingsMap.values()).map((a) =>
                          typeof a.holding_value === "object" ? 0 : a.holding_value
                        ),
                        backgroundColor: [
                          "rgba(134,85,255,0.98)",
                          "rgba(24,189,202,0.9)",
                          "rgba(255,174,63,0.95)",
                          "rgba(80,216,255,0.88)",
                          "rgba(253,238,117,0.85)",
                          "rgba(255,124,163,0.92)",
                          "rgba(70,201,156,0.91)",
                          "rgba(249,100,67,0.93)",
                        ],
                        borderWidth: 4,
                        borderColor: "#fff",
                        hoverOffset: 10,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          color: "#5c6698",
                          font: { size: 13, weight: "bold" },
                          boxWidth: 20,
                          padding: 18,
                          usePointStyle: true,
                        },
                      },
                      tooltip: {
                        backgroundColor: "#fff",
                        titleColor: "#8357ff",
                        bodyColor: "#5c8e74",
                        borderColor: "#c2aaff",
                        borderWidth: 2,
                        bodyFont: { weight: "bold", size: 13 }
                      }
                    },
                    cutout: 82,
                  }}
                />
                <div className="absolute left-0 top-0 w-full h-full flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[2.2rem] text-[#8471ff]/10 font-black tracking-widest select-none">
                    <GiWallet />
                  </span>
                </div>
              </div>
            </div>
            {/* Timeline investment visual */}
            <div className="relative bg-gradient-to-b from-[#e1f2ff] via-[#fffdf9] to-[#d3f6ed] shadow-2xl rounded-3xl p-8 flex flex-col items-center col-span-3 border-2 border-[#e4eaff] overflow-visible min-h-[350px]">
              <span className="absolute -top-12 right-0 w-40 h-40 bg-gradient-to-tr from-[#ffbca5] via-[#f9f3fc] to-[#73d6ca] opacity-30 rounded-full"></span>
              <div className="mb-6 flex items-center gap-2 z-10">
                <AiOutlineFundProjectionScreen className="text-[#41cee8] text-2xl drop-shadow-lg" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-l from-[#299da8] to-[#4175EB]">Investment Timeline</span>
              </div>
              <div className="w-full overflow-x-auto relative z-10" style={{ minHeight: "220px" }}>
                <Line
                  data={{
                    labels: filteredTransactions.map((t) =>
                      typeof t.date === "object" ? "-" : t.date
                    ),
                    datasets: [
                      {
                        label: "Invested",
                        data: filteredTransactions.map((t) =>
                          t.type === "buy"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(63,163,255,0.05)",
                        borderColor: "#29B8C6",
                        fill: true,
                        pointRadius: 3.5,
                        pointStyle: 'rectRot',
                        pointBackgroundColor: "#2edbeb",
                        pointBorderWidth: 2,
                        tension: 0.36,
                      },
                      {
                        label: "Withdrawn",
                        data: filteredTransactions.map((t) =>
                          t.type === "withdraw"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(232,247,225,0.18)",
                        borderColor: "#7bedaf",
                        fill: true,
                        pointRadius: 3.5,
                        pointStyle: 'star',
                        pointBackgroundColor: "#7bedaf",
                        pointBorderWidth: 2,
                        tension: 0.33,
                      },
                      {
                        label: "Sold",
                        data: filteredTransactions.map((t) =>
                          t.type === "sell"
                            ? typeof t.amount === "object"
                              ? 0
                              : t.amount
                            : 0
                        ),
                        backgroundColor: "rgba(255,182,203,0.18)",
                        borderColor: "#ff417a",
                        fill: true,
                        pointRadius: 3,
                        pointStyle: 'cross',
                        pointBackgroundColor: "#ff417a",
                        pointBorderWidth: 2,
                        tension: 0.28,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: "top",
                        labels: {
                          color: "#668ec3", font: { size: 14, weight: "bold" },
                          boxWidth: 22, usePointStyle: true, padding: 16
                        }
                      },
                      tooltip: {
                        backgroundColor: "#fff",
                        titleColor: "#5646c5",
                        bodyColor: "#34aea3",
                        borderColor: "#b4d8fe",
                        borderWidth: 2,
                        bodyFont: { weight: "bold", size: 14 }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: { color: "#3996b3", font: { size: 12, weight: 600 } },
                        grid: { color: "#f0fafb" }
                      },
                      x: {
                        ticks: { color: "#6b6f80", font: { size: 12, weight: 700 } },
                        grid: { color: "#f4f8fa" },
                      },
                    },
                    maintainAspectRatio: false,
                  }}
                  height={220}
                />
              </div>
            </div>
            {/* Bar: Holdings by Stock */}
            {barData.labels.length > 1 && (
              <div className="relative min-h-[360px] mt-10 bg-gradient-to-bl from-[#eaf6ff] via-[#fff0fc] to-[#daf1e9] shadow-[0_6px_32px_0_rgba(34,178,190,0.1)] rounded-[2.5rem] px-10 py-10 flex flex-col items-center lg:col-span-5 overflow-hidden border-2 border-[#cddbee] isolate">
                <span className="absolute left-10 -top-16 w-64 h-64 bg-gradient-to-tl from-[#38eeff] via-[#aadcfc] to-[#ffade9] opacity-20 rounded-full blur-md pointer-events-none z-0"></span>
                <span className="absolute -right-12 bottom-2 w-44 h-44 bg-gradient-to-br from-[#ddffe4] via-[#e9dfff] to-[#fed894] opacity-20 rounded-full blur-md pointer-events-none z-0"></span>
                <div className="absolute right-12 top-6 text-[#a1c6fa] pointer-events-none select-none z-0 scale-125">
                  <AiOutlineFundProjectionScreen size={58} opacity={0.09} />
                </div>
                <div className="mb-7 flex flex-col items-center z-10">
                  <span className="block text-[17px] font-extrabold tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-[#32C6DC] via-[#488cfb] to-[#845ec2] mb-2 flex items-center gap-2 drop-shadow">
                    <GiWallet className="text-[#57cab2]/80" size={26}/> Holdings by Stock
                  </span>
                  <div className="w-24 h-2 rounded-full bg-gradient-to-r from-[#32C6DC] via-[#488cfb] to-[#845ec2] mt-2 mb-2 opacity-80 shadow-lg" />
                </div>
                <div className="w-full relative z-10 flex flex-col items-center" style={{ minHeight: "220px" }}>
                  <div className="absolute top-3 left-4">
                    <span className="inline-flex items-center gap-1 text-xs bg-[#f4f7ec] text-[#269972] px-4 py-1 rounded-lg shadow-sm border border-[#cbede7] font-bold uppercase tracking-widest animate-bounce-slow">
                      <FaRegMoneyBillAlt className="mr-1" />Value
                    </span>
                  </div>
                  <Bar
                    data={barData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function(context: any) {
                              return `₹${Number(context.raw).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
                            }
                          },
                          backgroundColor: "#fff",
                          titleColor: "#0a71ed",
                          bodyColor: "#53b181",
                          borderColor: "#91f3d3",
                          borderWidth: 2,
                          cornerRadius: 8,
                          padding: 13
                        },
                        title: { display: false },
                      },
                      scales: {
                        x: {
                          grid: { color: "#ecf6fc" },
                          ticks: { color: "#4678a7", font: { weight: 700, size: 14 } },
                          border: { color: "#e8eef7" },
                        },
                        y: {
                          grid: { color: "#f3fcf8" },
                          ticks: {
                            color: "#269972",
                            font: { weight: 700, size: 12 },
                            callback: (val: number | string) => Number(val).toLocaleString("en-IN")
                          },
                          beginAtZero: true,
                          border: { color: "#e8eef7" }
                        },
                      },
                      maintainAspectRatio: false,
                      animation: {
                        duration: 1450,
                        easing: "easeOutElastic",
                      },
                    }}
                    height={230}
                  />
                </div>
              </div>
            )}
            {/* ADVANCED: Yearly overview - mini table */}
            <div className="col-span-full mt-8">
              <div className="bg-white rounded-lg shadow flex flex-col md:flex-row items-center md:justify-between p-4 gap-5 border border-[#e4e9f7]">
                <span className="font-bold text-[#467b99] flex items-center gap-2">
                  <TbHistory className="inline text-[#b48aea]"/> Yearly Investment Summary
                </span>
                <div className="overflow-x-auto">
                  <table className="text-xs min-w-[350px] text-[#657697] mx-auto">
                    <thead>
                      <tr>
                        {Object.keys(getYearlySummary()).map(yr => (
                          <th key={yr} className="px-3 py-2">{yr}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {Object.values(getYearlySummary()).map((amt, i) => (
                          <td key={i} className="px-3 py-1 font-bold">{formatMoney(amt, { compact: true, symbol: true })}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <span className="hidden md:inline text-xs text-[#8ca8e2]">Scroll above for monthly & other visualizations.</span>
              </div>
            </div>
          </section>
        </main>
      </div>
     
      {/* Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction.transaction}
          onClose={() => setSelectedTransaction(null)}
          stock={selectedTransaction.stock}
          category={selectedTransaction.category}
          party={selectedTransaction.party}
        />
      )}
      {/* Sticky footer with legend and info */}
      <footer className="sticky left-0 bottom-0 w-full z-50 shadow-[0_-3px_16px_-4px_#adc7f744] bg-[#ecf5fd] px-8 py-2 flex flex-wrap items-center text-[13px] gap-7 font-semibold text-[#436095] mr-[350px]">
        <div className="flex gap-3 items-center">
          <FaRegArrowAltCircleDown className="text-[#29b87f]" /> Withdraw
          <span className="mx-2">|</span>
          <FaRegArrowAltCircleUp className="text-[#ed0e25]" /> Sell
          <span className="mx-2">|</span>
          <IoWalletSharp className="text-[#F49A40]" /> Invest
        </div>
        <span className="flex-1 text-[#7da6c1] ml-3 min-w-[160px]">
          Tip: Use advanced filters, quick presets & dynamic charts to analyze your investments deeper!
        </span>
      </footer>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80">
          <div className="bg-gradient-to-tr from-[#ecfcff] via-[#e3eafe] to-[#f9fbfe] p-10 rounded-2xl shadow-lg flex flex-col items-center">
            <span className="text-2xl mb-1 text-[#2296ec] animate-bounce">
              <TbHistory />
            </span>
            <span className="font-bold text-[#357fb2] text-lg tracking-wider mb-2">Loading data...</span>
            <span className="text-[#8dbad6] text-xs">Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserHistory;
